// Drop-handler helpers for the canvas-first interactive editor (Task 18).
//
// The MIME type, %-position math, and active-scene InteractivePlugin lookup
// are split out as pure functions so the drop behaviour can be unit-tested
// without mounting the heavy EditorCanvasSli class (interact.js, ResizeDetector,
// hundreds of required props). EditorCanvasSli wires these onto the canvas
// container's onDragOver/onDrop and dispatches addInteractiveItem.

export const INTERACTIVE_ITEM_MIME = "application/x-aidea-interactive-item";

// Default footprint for a dropped interactive item, in % of the canvas.
// Matches the size used by the click-to-add path in InteractiveLibraryRail
// (which uses a wider center placement) closely enough to feel consistent
// but is tuned for cursor-anchored placement (item centered under cursor).
const DEFAULT_W = 30;
const DEFAULT_H = 25;

function clamp(v, min, max) {
  if (v < min) return min;
  if (v > max) return max;
  return v;
}

/**
 * Parse the interactive-item payload off a DataTransfer-like object.
 * Returns the parsed item or null if absent / malformed.
 */
export function parseInteractiveItemPayload(dataTransfer) {
  if (!dataTransfer || typeof dataTransfer.getData !== "function") return null;
  const raw = dataTransfer.getData(INTERACTIVE_ITEM_MIME);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch (_) {
    return null;
  }
}

/**
 * Compute a %-based drop position from cursor coordinates relative to the
 * canvas rect, centering a default-sized box on the cursor and clamping so
 * the box never extends past the canvas edges.
 */
export function computeDropPosition(clientX, clientY, rect) {
  const w = DEFAULT_W;
  const h = DEFAULT_H;
  const xPct = ((clientX - rect.left) / rect.width) * 100;
  const yPct = ((clientY - rect.top) / rect.height) * 100;
  const x = clamp(xPct - w / 2, 0, 100 - w);
  const y = clamp(yPct - h / 2, 0, 100 - h);
  return { x, y, w, h };
}

/**
 * Resolve the InteractivePlugin box id on the currently active scene, or
 * null if no such box exists. Mirrors the selector used by
 * InteractiveLibraryRail.
 */
export function findInteractiveBoxIdInState(state) {
  const present = (state && state.undoGroup && state.undoGroup.present) || {};
  const sceneId = present.navItemSelected;
  if (!sceneId || sceneId === 0) return null;
  const scene = (present.navItemsById || {})[sceneId];
  if (!scene || !Array.isArray(scene.boxes)) return null;
  const pluginsById = present.pluginToolbarsById || {};
  // Plugin identity lives on pluginToolbarsById[boxId].pluginId — boxesById[id].content
  // is an opaque object ({}) in this codebase, not the plugin name.
  const id = scene.boxes.find(
    (bId) => pluginsById[bId] && pluginsById[bId].pluginId === "InteractivePlugin"
  );
  return id || null;
}

/**
 * Convenience: given a dataTransfer + cursor coords + canvas rect, return a
 * fully-positioned item ready to be passed to addInteractiveItem, or null if
 * the payload is missing.
 */
export function buildAddInteractiveItemFromDrop(dataTransfer, clientX, clientY, rect) {
  const item = parseInteractiveItemPayload(dataTransfer);
  if (!item) return null;
  const position = computeDropPosition(clientX, clientY, rect);
  return { ...item, position };
}
